/**
 * @file create-warehouse.tsx
 * @module app/modules/warehouse/create-warehouse
 * @description Warehouse creation form in UTrade Admin.
 * Handles warehouse details, city/district assignment, manager selection, and create submission.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.2.1
 */

import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { CustomSelectTable2 } from '../../custom/Select/custom-select-table';
import clsx from 'clsx';
import AutoComplete from '../../custom/autoComplete';
import { PermissionModule } from '../../../utils/constants';
import APICallService from '../../../api/apiCallService';
import { master, multipleWarehouse } from '../../../api/apiEndPoints';
import { multipleWarehouseJSON } from '../../../api/apiJSON/multipleWarehouse';
import { success } from '../../../Global/toast';
import { useNavigate } from 'react-router-dom';
import Method from '../../../utils/methods';
import Validations from '../../../utils/validations';
import { getKey } from '../../../Global/history';
import { warehouse as warehouseStoreString } from '../../../utils/storeString';
import { useAuth } from '../auth';

const CreateWarehouse = () => {
  const { currentUser } = useAuth();
  let tempWarehouse: any = getKey(warehouseStoreString.warehouseRef);
  tempWarehouse = JSON.parse(tempWarehouse);
  const navigate = useNavigate();
  const [, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [warehouseData, setWarehouseData] = useState<any>({
    name: '',
    email: '',
    phone: '',
    phoneCountry: '+255',
    address: '',
    lat: '',
    lng: '',
    city: [],
    district: [],
    manager: [],
  });
  const [validations, setValidations] = useState<any>({
    name: false,
    email: false,
    phone: false,
    address: false,
    districts: false,
    managers: false,
    city: false,
  });
  const [cityList, setCityList] = useState<any>([]);
  const [districtOption, setDistrictOption] = useState([]);
  const [users, setUsers] = useState<any>([]);
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchDistrictAndCity();
    fetchUser();
  }, []);

  const fetchDistrictAndCity = async () => {
    let params: any = {};
    if (tempWarehouse?._id) {
      params.warehouse = tempWarehouse._id;
    }
    setLoading(true);
    const apiService = new APICallService(
      master.listCityAndDistrict,
      params,
      '',
      '',
      '',
      '',
      '',
      tempWarehouse?._id || null
    );
    const response = await apiService.callAPI();
    let data: any = [];
    if (response.records.length) {
      response.records.forEach((val: any) => {
        data.push({
          value: val._id,
          label: val.name,
          title: val.name,
          district: val.districts,
        });
      });
    }
    setCityList(data);
    setLoading(false);
  };
  const handleChange = (value: any, name: keyof any) => {
    const temp = { ...warehouseData };
    const tempValidation: any = { ...validations };
    temp[name] = value;
    tempValidation[name] = value.trim().length === 0;
    setValidations(tempValidation);
    setWarehouseData(temp);
  };
  const handleManagerChange = (data: any) => {
    const tempValidation = { ...validations };
    setWarehouseData((prevData: any) => ({
      ...prevData,
      manager: data,
    }));
    if (data.length) {
      tempValidation.managers = false;
    } else {
      tempValidation.managers = true;
    }
    setValidations(tempValidation);
  };
  const handleDistrictChange = (data: any) => {
    const tempValidation = { ...validations };
    setWarehouseData((prevData: any) => ({
      ...prevData,
      district: data,
    }));
    if (data.length) {
      tempValidation.districts = false;
    } else {
      tempValidation.districts = true;
    }
    setValidations(tempValidation);
  };

  const handleCityChange = (data: any) => {
    const tempValidation = { ...validations };
    let tempDistrict: any = [];
    data.forEach((val: any) =>
      val.district.forEach((district: any) => {
        tempDistrict.push({
          value: district._id,
          label: district.name,
          title: district.name,
        });
      })
    );
    setWarehouseData((prevData: any) => ({
      ...prevData,
      city: data,
      district: prevData.district.filter((val: any) =>
        tempDistrict.find((val1: any) => val1.value === val.value)
      ),
    }));
    if (data.length) {
      tempValidation.city = false;
    } else {
      tempValidation.city = true;
      tempValidation.districts = true;
    }
    setDistrictOption(tempDistrict);
    setValidations(tempValidation);
  };

  /**
   * Handles changes to the address field, updating the warehouse data and validation state.
   *
   * @param value The address value
   * @param lat The latitude of the address
   * @param lng The longitude of the address
   */
  const handleAddressChange = async (value: string, lat: any, lng: any) => {
    let tempValidation: any = { ...validations };
    let temp: any = { ...warehouseData };
    temp['address'] = value.trimStart();
    temp['lat'] = lat;
    temp['lng'] = lng;
    tempValidation['address'] = !value.length;
    setWarehouseData(temp);
    setValidations(tempValidation);
  };

  const fetchUser = async () => {
    setLoading(true);
    const params: any = {
      sortKey: 'createdAt',
      sortOrder: -1,
      needCount: true,
      warehouseManager: 2,
    };
    if (tempWarehouse?._id) {
      params.warehouse = tempWarehouse._id;
    }
    let apiService = new APICallService(
      master.getUsers,
      params,
      '',
      '',
      '',
      '',
      PermissionModule.WarehouseManger,
      tempWarehouse?._id
    );
    let response = await apiService.callAPI();
    let data: any = [...users];
    if (response.records.length) {
      response.records.forEach((val: any) => {
        data.push({
          value: val._id,
          label: val.name,
          title: val.name,
        });
      });
    }
    setUsers(data);
    setLoading(false);
  };
  const handleSave = async () => {
    const tempValidation = { ...validations };
    tempValidation.name = warehouseData.name.trim().length === 0;
    tempValidation.email = warehouseData.email.trim().length === 0;
    tempValidation.phone = warehouseData.phone.trim().length === 0;
    tempValidation.address = warehouseData.address.trim().length === 0;
    tempValidation.districts = warehouseData.district.length === 0;
    tempValidation.city = warehouseData.city.length === 0;
    tempValidation.managers = warehouseData.manager.length === 0;
    const isValid = await Validations.validateObject(tempValidation);
    if (isValid) {
      setIsSubmitting(true);
      const apiService = new APICallService(
        multipleWarehouse.addWarehouse,
        multipleWarehouseJSON.addWarehouse(warehouseData),
        '',
        '',
        '',
        '',
        PermissionModule.WarehouseManger,
        tempWarehouse?._id
      );
      const response = await apiService.callAPI();
      if (response) {
        success('Warehouse created successfully!');
        navigate('/warehouse/all-warehouse');
        
      }
      setIsSubmitting(false);
    }
    setValidations(tempValidation);
  };
  return (
    <div className="p-5">
      <Row className="align-items-center">
        <Col xs={12} className="mb-5">
          <h1 className="fs-22 fw-bolder">Add new warehouse</h1>
        </Col>
        <Col xs={12}>
          <Card className="bg-light border mb-7">
            <Card.Body className="px-7">
              <Row className="gx-lg-9 gx-6 gy-6">
                <Col md={6}>
                  <Row className="gy-2 align-items-center">
                    <Col sm={4}>
                      <label htmlFor="" className="fs-16 fw-500">
                        Name
                      </label>
                    </Col>
                    <Col sm={8}>
                      <Form.Control
                        className={clsx('form-control-custom bg-white h-60px', {
                          ' border border-1 border-danger': validations.name,
                        })}
                        type="text"
                        placeholder="Type here..."
                        name="name"
                        value={warehouseData.name}
                        onChange={(e: any) =>
                          handleChange(e.target.value, 'name')
                        }
                      />
                    </Col>
                  </Row>
                </Col>
                <Col md={6}>
                  <Row className="gy-2 align-items-center">
                    <Col sm={4}>
                      <label htmlFor="" className="fs-16 fw-500">
                        Warehouse Contact number
                      </label>
                    </Col>
                    <Col sm={8}>
                      <InputGroup
                        size="lg"
                        className={`${
                          validations.phone
                            ? 'border border-1 border-danger rounded-3'
                            : ''
                        }`}
                      >
                        <InputGroup.Text id="inputGroup-sizing-lg">
                          +255
                        </InputGroup.Text>
                        <Form.Control
                          className={clsx(
                            'form-control-custom bg-white h-60px'
                          )}
                          type="number"
                          placeholder="Type here…"
                          name="phone"
                          value={warehouseData.phone}
                          onChange={(e: any) =>
                            handleChange(e.target.value, 'phone')
                          }
                        />
                      </InputGroup>
                    </Col>
                  </Row>
                </Col>
                <Col md={6}>
                  <Row className="gy-2 align-items-center">
                    <Col sm={4}>
                      <label htmlFor="" className="fs-16 fw-500">
                        Address
                      </label>
                    </Col>
                    <Col sm={8}>
                      <AutoComplete
                        address={warehouseData.address}
                        handleAddressChange={handleAddressChange}
                        lat={warehouseData.lat}
                        lng={warehouseData.lng}
                        border={clsx(validations.address ? '#e55451' : '')}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col md={6}>
                  <Row className="gy-2 align-items-center">
                    <Col sm={4}>
                      <label htmlFor="" className="fs-16 fw-500">
                        Email
                      </label>
                    </Col>
                    <Col sm={8}>
                      <Form.Control
                        className={clsx('form-control-custom bg-white h-60px', {
                          'border-danger': validations.email,
                        })}
                        type="text"
                        placeholder="Type here..."
                        name="email"
                        value={warehouseData.email}
                        onChange={(e: any) =>
                          handleChange(e.target.value, 'email')
                        }
                      />
                    </Col>
                  </Row>
                </Col>
                <Col md={12}>
                  <Row className="gy-2 align-items-center">
                    <Col sm={2}>
                      <label htmlFor="" className="fs-16 fw-500">
                        Assign city
                      </label>
                    </Col>
                    <Col sm={10}>
                      <CustomSelectTable2
                        border={clsx(validations.city ? '#e55451' : '')}
                        backgroundColor="white"
                        multiValueBackground="#e7f1fd"
                        indicatorDisplay="none"
                        display="none"
                        isMulti={true}
                        menuIsOpen={false}
                        controlFontSize="1.077rem"
                        minHieight="60px"
                        options={cityList}
                        onChange={handleCityChange}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col md={12}>
                  <Row className="gy-2 align-items-center">
                    <Col sm={2}>
                      <label htmlFor="" className="fs-16 fw-500">
                        Assign districts
                      </label>
                    </Col>
                    <Col sm={10}>
                      <CustomSelectTable2
                        border={clsx(validations.districts ? '#e55451' : '')}
                        backgroundColor="white"
                        multiValueBackground="#e7f1fd"
                        indicatorDisplay="none"
                        display="none"
                        isMulti={true}
                        menuIsOpen={false}
                        controlFontSize="1.077rem"
                        minHieight="60px"
                        options={districtOption}
                        value={warehouseData.district}
                        onChange={handleDistrictChange}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col md={12}>
                  <Row className="gy-2 align-items-center">
                    <Col sm={2}>
                      <label htmlFor="" className="fs-16 fw-500">
                        Assign Managers
                      </label>
                    </Col>
                    <Col sm={10}>
                      <CustomSelectTable2
                        border={clsx(validations.managers ? '#e55451' : '')}
                        backgroundColor="white"
                        multiValueBackground="#e7f1fd"
                        indicatorDisplay="none"
                        display="none"
                        isMulti={true}
                        menuIsOpen={false}
                        controlFontSize="1.077rem"
                        minHieight="60px"
                        options={users}
                        onChange={handleManagerChange}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Row>
            <Col sm={3}>
              <Button
                className="uw-btn uw-btn-primary"
                disabled={isSubmitting}
                onClick={handleSave}
              >
                {!isSubmitting && <span className="indicator-label">Save</span>}
                {isSubmitting && (
                  <span
                    className="indicator-progress"
                    style={{ display: 'block' }}
                  >
                    Please wait...
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                )}
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};
export default CreateWarehouse;
